// Pure helpers for the Sessions detail navigation.
//
// A "gate" is one challenge from the session-history payload, carrying:
//   { session_id, challenge_id, challenge_type, horizon_offset, horizon,
//     gate_closure, start_datetime, end_datetime, use_case }
//
// Horizon identity is `${challenge_type}:${horizon_offset}` so intraday and
// every day-ahead horizon are distinct, even when they share a market session.

const horizonKey = (gate) => `${gate.challenge_type}:${gate.horizon_offset}`;

/** Public alias: horizon identity for a single gate. */
export const gateHorizonKey = horizonKey;

const gateTime = (gate) => new Date(gate.gate_closure || 0).getTime();

/** Distinct horizons for a day, Intraday first then ascending horizon_offset. */
export const deriveHorizonOptions = (gates = []) => {
  const byKey = new Map();
  for (const gate of gates) {
    const key = horizonKey(gate);
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: gate.horizon,
        challengeType: gate.challenge_type,
        horizonOffset: gate.horizon_offset,
      });
    }
  }
  return [...byKey.values()].sort((a, b) => {
    const ai = a.challengeType === 'intraday' || Number(a.horizonOffset) === 0;
    const bi = b.challengeType === 'intraday' || Number(b.horizonOffset) === 0;
    if (ai !== bi) return ai ? -1 : 1;
    return Number(a.horizonOffset) - Number(b.horizonOffset);
  });
};

/** Gates matching a horizon key, latest gate_closure first. */
export const deriveGateOptions = (gates = [], key) =>
  gates
    .filter((gate) => horizonKey(gate) === key)
    .sort((a, b) => gateTime(b) - gateTime(a));

/** Default horizon: Intraday if present, else the most recently closed horizon. */
export const pickDefaultHorizon = (gates = []) => {
  const options = deriveHorizonOptions(gates);
  if (options.length === 0) return null;

  const intraday = options.find(
    (o) => o.challengeType === 'intraday' || Number(o.horizonOffset) === 0
  );
  if (intraday) return intraday;

  // Otherwise: the horizon whose latest gate closure is most recent
  // (tie-break: smallest horizon_offset).
  let best = null;
  let bestTime = -Infinity;
  for (const option of options) {
    const latest = Math.max(...deriveGateOptions(gates, option.key).map(gateTime));
    if (
      latest > bestTime ||
      (latest === bestTime && Number(option.horizonOffset) < Number(best.horizonOffset))
    ) {
      best = option;
      bestTime = latest;
    }
  }
  return best;
};

/** Default gate for a horizon: the latest gate closure. */
export const pickDefaultGate = (gates = [], key) => deriveGateOptions(gates, key)[0] || null;

/**
 * Horizons with their gates, ready for the day panel's navigation tree.
 * Each gate carries display fields (closure datetime, open/closed status).
 * `now` is injectable for deterministic tests.
 */
export const deriveHorizonGroups = (gates = [], now = Date.now()) =>
  deriveHorizonOptions(gates).map((option) => ({
    ...option,
    gates: deriveGateOptions(gates, option.key).map((gate) => ({
      challengeId: gate.challenge_id,
      gateClosure: gate.gate_closure,
      startDatetime: gate.start_datetime,
      endDatetime: gate.end_datetime,
      isOpen: gateTime(gate) > now,
    })),
  }));

/** Summary of a target day, derived from its gates (no fetch needed). */
export const deriveDayInfo = (group = {}) => {
  const gates = group.gates || [];
  const latestGateClosure = gates.reduce((latest, gate) => {
    if (!gate.gate_closure) return latest;
    if (!latest || gateTime(gate) > new Date(latest).getTime()) return gate.gate_closure;
    return latest;
  }, null);
  return {
    targetDay: group.target_day,
    sessionCount: new Set(gates.map((gate) => gate.session_id)).size,
    horizonLabels: deriveHorizonOptions(gates).map((o) => o.label),
    latestGateClosure,
    useCase: gates[0]?.use_case || null,
  };
};
